import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Warning, Trash, Key, Eye, EyeSlash, CheckCircle, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

function strengthOf(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score; // 0..5
}
const STRENGTH_LABEL = ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"];
const STRENGTH_COLOR = ["bg-rose-500", "bg-rose-500", "bg-amber-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"];

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const forceMode = user?.must_change_password || searchParams.get("force") === "1";
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";
  const canBackup = ["admin", "director"].includes(user?.role);
  const [backupBusy, setBackupBusy] = useState(false);

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const r = await api.get("/admin/backup.json", { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const filename = `paramskills-erp-backup-${stamp}.json.gz`;
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep it somewhere safe");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Backup failed");
    } finally {
      setBackupBusy(false);
    }
  };

  // Change password state
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const strength = strengthOf(pw.new_password);
  const canChange =
    pw.current_password.length > 0 &&
    pw.new_password.length >= 8 &&
    pw.new_password === pw.confirm_password &&
    pw.new_password !== pw.current_password;

  const changePw = async (e) => {
    e.preventDefault();
    if (!canChange) return;
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated. Stay signed in — your session was refreshed.");
      setPw({ current_password: "", new_password: "", confirm_password: "" });
      if (forceMode) {
        setUser({ ...user, must_change_password: false });
        setTimeout(() => nav("/dashboard"), 500);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  const wipe = async () => {
    if (confirm !== "WIPE ALL DATA") return toast.error("Type WIPE ALL DATA to confirm");
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-data");
      const total = Object.values(r.data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} records. You will be logged out.`);
      setOpen(false);
      setTimeout(async () => {
        await logout();
        nav("/login");
      }, 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl space-y-6" data-testid="settings-root">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Administration</div>
        <h1 className="font-heading text-4xl mt-1 tracking-tight">Settings</h1>
      </div>

      {forceMode && (
        <div className="border-2 border-amber-500 bg-amber-50 p-6 flex gap-3" data-testid="force-change-banner">
          <Warning size={20} weight="bold" className="text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-amber-900">Set your own password to continue</div>
            <div className="text-sm text-amber-800 mt-1">
              This account is using a temporary password issued by your administrator.
              For security, please choose a new password below before using the rest of the app.
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 p-6 space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Signed in as</div>
        <div className="font-medium">{user?.name}</div>
        <div className="text-sm text-zinc-500">{user?.email} · {user?.role}</div>
      </div>

      {/* Change Password */}
      <div className="bg-white border border-zinc-200 p-6 space-y-4" data-testid="change-password-block">
        <div className="flex items-center gap-2">
          <Key size={18} weight="regular" />
          <div className="font-heading text-lg">Change Password</div>
        </div>
        <p className="text-sm text-zinc-500">
          Rotate your password anytime. A strong password mixes upper- and lower-case
          letters, digits and symbols, and is at least 12 characters long.
        </p>
        <form onSubmit={changePw} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Current Password</Label>
            <div className="relative mt-2">
              <Input
                type={showCur ? "text" : "password"}
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                className="rounded-sm h-10 pr-10 border-zinc-300"
                data-testid="current-password"
                autoComplete="current-password"
                required
              />
              <button type="button" onClick={() => setShowCur(!showCur)}
                      className="absolute right-2 top-2 text-zinc-500 hover:text-zinc-900 p-1"
                      aria-label="Toggle current password visibility">
                {showCur ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">New Password</Label>
            <div className="relative mt-2">
              <Input
                type={showNew ? "text" : "password"}
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                className="rounded-sm h-10 pr-10 border-zinc-300"
                data-testid="new-password"
                autoComplete="new-password"
                required
                minLength={8}
              />
              <button type="button" onClick={() => setShowNew(!showNew)}
                      className="absolute right-2 top-2 text-zinc-500 hover:text-zinc-900 p-1"
                      aria-label="Toggle new password visibility">
                {showNew ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {pw.new_password && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1 bg-zinc-100 flex gap-0.5">
                  {[0,1,2,3,4].map((i) => (
                    <div key={i} className={`h-full flex-1 ${i < strength ? STRENGTH_COLOR[strength] : "bg-zinc-100"}`} />
                  ))}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">{STRENGTH_LABEL[strength]}</span>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest">Confirm New Password</Label>
            <Input
              type={showNew ? "text" : "password"}
              value={pw.confirm_password}
              onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })}
              className="rounded-sm h-10 mt-2 border-zinc-300"
              data-testid="confirm-password"
              autoComplete="new-password"
              required
            />
            {pw.confirm_password && pw.confirm_password !== pw.new_password && (
              <div className="mt-1 text-xs text-rose-700">Passwords don't match</div>
            )}
            {pw.confirm_password && pw.confirm_password === pw.new_password && pw.new_password.length >= 8 && (
              <div className="mt-1 text-xs text-emerald-700 flex items-center gap-1"><CheckCircle size={12} /> Matches</div>
            )}
          </div>

          <div className="md:col-span-2 flex justify-end">
            <Button
              type="submit"
              disabled={!canChange || pwBusy}
              data-testid="change-password-submit"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10"
            >
              {pwBusy ? "Updating…" : "Update Password"}
            </Button>
          </div>
        </form>
      </div>

      {canBackup && (
        <div className="bg-white border-2 border-emerald-600 p-6 space-y-4" data-testid="backup-block">
          <div className="flex items-center gap-2">
            <DownloadSimple size={20} weight="bold" className="text-emerald-700" />
            <div className="font-heading text-lg text-emerald-900">Database Backup</div>
          </div>
          <p className="text-sm text-zinc-700">
            Download a full, timestamped snapshot of the ERP database — every centre,
            user, expense, income, asset, vendor, budget, notification, audit entry
            and file attachment. The file is gzipped JSON so it stays small enough
            to email or store in a password-protected folder.
          </p>
          <div className="text-xs bg-emerald-50/50 border border-emerald-200 p-3 space-y-1">
            <div className="uppercase tracking-widest text-[10px] text-emerald-900">Recommended cadence</div>
            <div>• Weekly, or before any admin action that touches many records</div>
            <div>• Store the file in a password-protected drive or cloud folder</div>
            <div>• Password hashes are included — treat this file as highly sensitive</div>
          </div>
          <Button
            onClick={downloadBackup}
            disabled={backupBusy}
            data-testid="backup-download-btn"
            className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
          >
            <DownloadSimple size={14} />
            {backupBusy ? "Preparing backup…" : "Download Backup Now"}
          </Button>
        </div>
      )}

      {isAdmin && (
        <div className="border-2 border-rose-600 bg-rose-50 p-6 space-y-4" data-testid="danger-zone">
          <div className="flex items-center gap-2">
            <Warning size={20} weight="bold" className="text-rose-700" />
            <div className="font-heading text-lg text-rose-900">Danger Zone</div>
          </div>
          <p className="text-sm text-rose-900">
            Wipe all demo/operational data — centres, expenses, income, budgets, vendors,
            notifications, emails, audit logs, uploaded files, and every non-admin user.
            Your admin account will survive. Automatic demo re-seeding will also be
            disabled so the environment stays clean for real data.
          </p>
          <div className="text-xs text-rose-800 bg-white/50 border border-rose-300 p-3 space-y-1">
            <div className="uppercase tracking-widest text-[10px]">Next steps after wipe</div>
            <div>1. Create Centre(s) via <span className="font-mono">Centres → New Centre</span></div>
            <div>2. Create Users with role Centre Manager and assign a Centre</div>
            <div>3. Log in as that Centre Manager and start uploading expenses</div>
          </div>
          <Button onClick={() => { setOpen(true); setConfirm(""); }}
                  data-testid="open-wipe-btn"
                  className="rounded-sm bg-rose-700 hover:bg-rose-800 text-white gap-2">
            <Trash size={14} /> Wipe All Demo Data
          </Button>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-rose-800 flex items-center gap-2">
              <Warning weight="bold" /> Confirm Wipe
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-700">
            This action <span className="font-bold">cannot be undone</span>. It will delete every centre,
            expense, income record, user, vendor, notification, email log and audit entry
            in this environment. Your admin login stays.
          </p>
          <div>
            <Label className="text-xs uppercase tracking-widest">Type <span className="font-mono">WIPE ALL DATA</span> to confirm</Label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="wipe-confirm-input"
              placeholder="WIPE ALL DATA"
              className="rounded-sm h-10 mt-2 font-mono"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={wipe}
              disabled={busy || confirm !== "WIPE ALL DATA"}
              data-testid="confirm-wipe-btn"
              className="rounded-sm bg-rose-700 hover:bg-rose-800 text-white"
            >
              {busy ? "Wiping…" : "Wipe Everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
